
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Layers, AlertTriangle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { BulkUpdateDialog } from '@/components/candidates/BulkUpdateDialog';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Sentry, isSentryEnabled } from '@/sentry';
import {
  getCandidateStatusLabel,
  getEvaluationRoundLabel,
  type CandidateStatus,
  type EvaluationRound,
} from '@/types/enums';
import type { Tables } from '@/integrations/supabase/types';

type Candidate = Tables<'candidates'>;

interface BulkUpdateManagerProps {
  candidates: Candidate[];
  onUpdate: () => void;
  disabled?: boolean;
}

export const BulkUpdateManager = React.memo(({
  candidates = [], // Default to empty array to prevent undefined errors
  onUpdate,
  disabled = false,
}: BulkUpdateManagerProps) => {
  const { toast } = useToast();
  const [isBulkUpdateDialogOpen, setIsBulkUpdateDialogOpen] = useState(false);
  const [isBulkUpdateAlertDialogOpen, setIsBulkUpdateAlertDialogOpen] = useState(false);
  const [pendingBulkUpdateDetails, setPendingBulkUpdateDetails] = useState<{
    updateType: 'statusOnly' | 'statusAndRound';
    newStatus: string;
    newRound?: string;
    candidateIds: string[];
  } | null>(null);
  const [isUpdatingBulkCandidates, setIsUpdatingBulkCandidates] = useState(false);

  const handleBulkUpdateConfirm = (
    updateType: 'statusOnly' | 'statusAndRound',
    newStatus: string,
    newRound?: string
  ) => {
    const candidateIdsToUpdate = candidates.map((c) => c.id);
    if (candidateIdsToUpdate.length === 0) {
      toast({
        title: 'No Candidates Selected',
        description: 'Please filter candidates to apply bulk update.',
        variant: 'destructive',
      });
      return;
    }

    setPendingBulkUpdateDetails({
      updateType,
      newStatus,
      newRound,
      candidateIds: candidateIdsToUpdate,
    });
    setIsBulkUpdateDialogOpen(false);
    setIsBulkUpdateAlertDialogOpen(true);
  };

  const performBulkUpdate = async () => {
    if (!pendingBulkUpdateDetails || pendingBulkUpdateDetails.candidateIds.length === 0) {
      toast({
        title: 'Bulk Update Error',
        description: 'No candidates selected or details missing for bulk update.',
        variant: 'destructive',
      });
      setPendingBulkUpdateDetails(null);
      setIsBulkUpdateAlertDialogOpen(false);
      setIsUpdatingBulkCandidates(false);
      return;
    }

    setIsUpdatingBulkCandidates(true);

    const { updateType, newStatus, newRound, candidateIds } = pendingBulkUpdateDetails;

    let successfulUpdates = 0;
    let failedUpdates = 0;

    if (updateType === 'statusOnly') {
      const updatePromises = candidateIds.map((id) =>
        supabase.from('candidates').update({ status: newStatus }).eq('id', id)
      );
      const results = await Promise.allSettled(updatePromises);

      results.forEach((result) => {
        if (result.status === 'fulfilled' && !result.value.error) {
          successfulUpdates++;
        } else {
          failedUpdates++;
          const err = result.status === 'rejected' ? result.reason : result.value.error;
          console.error('Failed to update candidate status:', err);
          if (isSentryEnabled) Sentry.captureException(err);
        }
      });
    } else if (updateType === 'statusAndRound' && newRound) {
      const updatePromises = candidateIds.map((id) =>
        supabase
          .from('candidates')
          .update({ status: newStatus, current_round: newRound })
          .eq('id', id)
      );
      const results = await Promise.allSettled(updatePromises);
      results.forEach((result) => {
        if (result.status === 'fulfilled' && !result.value.error) {
          successfulUpdates++;
        } else {
          failedUpdates++;
          const err = result.status === 'rejected' ? result.reason : result.value.error;
          if (isSentryEnabled) Sentry.captureException(err);
        }
      });
    }

    toast({
      title: 'Bulk Update Completed',
      description: `Successfully updated ${successfulUpdates} candidates. ${
        failedUpdates > 0 ? `${failedUpdates} updates failed.` : ''
      }`,
      variant: failedUpdates > 0 ? 'destructive' : 'default',
    });

    setIsUpdatingBulkCandidates(false);
    setPendingBulkUpdateDetails(null);
    onUpdate();
  };

  return (
    <>
      <Button
        onClick={() => setIsBulkUpdateDialogOpen(true)}
        disabled={candidates.length === 0 || disabled || isUpdatingBulkCandidates}
      >
        <Layers className="h-4 w-4 mr-2" />
        Bulk Update ({candidates.length})
      </Button>

      <BulkUpdateDialog
        open={isBulkUpdateDialogOpen}
        onClose={() => {
          if (!isUpdatingBulkCandidates) {
            setIsBulkUpdateDialogOpen(false);
          }
        }}
        onConfirm={handleBulkUpdateConfirm}
        candidateCount={candidates.length}
      />

      {pendingBulkUpdateDetails && (
        <AlertDialog
          open={isBulkUpdateAlertDialogOpen}
          onOpenChange={(open) => {
            if (isUpdatingBulkCandidates && !open) return;
            setIsBulkUpdateAlertDialogOpen(open);
            if (!open) setPendingBulkUpdateDetails(null);
          }}
        >
          <AlertDialogContent className="max-h-[90vh] overflow-y-auto">
            <AlertDialogHeader>
              <AlertDialogTitle>
                <AlertTriangle className="inline-block mr-2 h-5 w-5 text-yellow-500" />
                Confirm Bulk Update
              </AlertDialogTitle>
              <AlertDialogDescription>
                {pendingBulkUpdateDetails.updateType === 'statusOnly' &&
                  `Are you sure you want to update the status of ${
                    pendingBulkUpdateDetails.candidateIds.length
                  } candidate(s) to "${getCandidateStatusLabel(
                    pendingBulkUpdateDetails.newStatus as CandidateStatus
                  )}"?`}
                {pendingBulkUpdateDetails.updateType === 'statusAndRound' &&
                  `Are you sure you want to update ${
                    pendingBulkUpdateDetails.candidateIds.length
                  } candidate(s) to status "${getCandidateStatusLabel(
                    pendingBulkUpdateDetails.newStatus as CandidateStatus
                  )}" and round "${getEvaluationRoundLabel(
                    pendingBulkUpdateDetails.newRound as EvaluationRound
                  )}"?`}
                <br /> This action cannot be undone easily.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel
                onClick={() => setPendingBulkUpdateDetails(null)}
                disabled={isUpdatingBulkCandidates}
              >
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction onClick={performBulkUpdate} disabled={isUpdatingBulkCandidates}>
                {isUpdatingBulkCandidates ? 'Updating...' : 'Confirm & Update'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      )}
    </>
  );
});

BulkUpdateManager.displayName = 'BulkUpdateManager';
